import React from 'react';
import framework from 'utils/framework';
import Events from 'utils/framework/Events';
const { wrapComponent } = framework;
import BaseClass from 'components/BaseClass';

import { Container } from 'components/ui';
import analyticsConsts from 'analytics/constants';
import BackToTopButton from 'components/BackToTopButton/BackToTopButton';
import localeUtils from 'utils/LanguageLocale';
import Actions from 'Actions';
import ProductActions from 'actions/ProductActions';
import AddToBasketActions from 'actions/AddToBasketActions';
import ProductPageSEO from 'components/ProductPage/ProductPageSEO/ProductPageSEO';
import DigitalProduct from 'components/ProductPage/Type/DigitalProduct/DigitalProduct';
import SDUProductPage from 'components/ProductPage/Type/DigitalProduct/SDUProductPage';
import RegularProduct from 'components/ProductPage/Type/RegularProduct';
import RewardProduct from 'components/ProductPage/Type/RewardProduct/RewardProduct';
import SetPageAnalyticsProps from 'components/Analytics';
import productPageBindings from 'analytics/bindingMethods/pages/productPage/productPageBindings';
import processEvent from 'analytics/processEvent';
import dateUtils from 'utils/Date';
import skuUtils from 'utils/Sku';
import skuHelpers from 'utils/skuHelpers';
import store from 'store/Store';
import urlUtils from 'utils/Url';
import userUtils from 'utils/User';
import Location from 'utils/Location';
import { PostLoad } from 'constants/events';
import MobileStickyMenu from 'components/ProductPage/MobileStickyMenu/MobileStickyMenu.ctrlr';
import { breakpoints } from 'style/config';
import actions from 'actions/Actions';
import { Pages } from 'constants/Pages';
import basketConstants from 'constants/Basket';
import BasketActionsEN from 'actions/locales/BasketActions_en_US.js';
import BasketActionsFR from 'actions/locales/BasketActions_fr_CA.js';
import ProductPageEN from 'pages/Product/locales/ProductPage_en_US.js';
import ProductPageFR from 'pages/Product/locales/ProductPage_fr_CA.js';

const getBasketText = localeUtils.getLocalisedDataFromResource(BasketActionsEN, BasketActionsFR);
const getText = localeUtils.getLocalisedDataFromResource(ProductPageEN, ProductPageFR);
const { BasketType, DEEPLINK_ADD_TO_BASKET_SOURCES } = basketConstants;
const {
    DEEPLINK_US_SMS, DEEPLINK_CA_SMS, DEEPLINK_US_EMAIL, DEEPLINK_CA_EMAIL, REPLEN_US_SMS, REPLEN_CA_SMS
} = DEEPLINK_ADD_TO_BASKET_SOURCES;

const {
    PRODUCT_IDS: { SAMPLE },
    SKU_ID_PARAM,
    skuTypes: { REWARD, ROUGE_REWARD_CARD, STANDARD, SDU }
} = skuUtils;
const {
    SKU_UPDATE_SOURCE: { QUERY_STRING }
} = ProductActions;

const SKU_DATA_READY = 'skuDataReady';
const USER_SPECIFIC_DATA_READY = 'userSpecificDataReady';

const ADD_TO_BASKET_PARAM = 'om_mmc';
const ADD_TO_BASKET_MAPPING = {
    [ADD_TO_BASKET_PARAM]: [DEEPLINK_US_SMS, DEEPLINK_CA_SMS, DEEPLINK_US_EMAIL, DEEPLINK_CA_EMAIL, REPLEN_US_SMS, REPLEN_CA_SMS]
};
const ADD_TO_BASKET_DEFAULT_QTY = 1;

class ProductPage extends BaseClass {
    constructor(props) {
        super(props);
        const {
            page: { product }
        } = store.getState();

        this.state = { product, showStickyMenu: false, alreadyTriedToAddProductToBasket: false };
    }

    shouldSendAnalytics = {
        [SKU_DATA_READY]: false,
        [USER_SPECIFIC_DATA_READY]: false
    };

    // GUAR-8124: Track if this is initial page load (true) or SPA navigation (false)
    isInitialPageLoad = true;

    // GUAR-8124: Fire ASYNC_PAGE_LOAD ONLY if product is in stock and has deliveryDate
    fireEddDateAnalytics = product => {
        const deliveryDate = product?.fulfillmentOptions?.deliveryOptions?.shipToHome?.promiseDate?.[0]?.deliveryDate;

        if (!product?.currentSku?.isOutOfStock && deliveryDate) {
            const eddData = dateUtils.getEddAnalyticsData(deliveryDate);

            if (eddData) {
                processEvent.process(analyticsConsts.ASYNC_PAGE_LOAD, {
                    data: {
                        eventStrings: [`event320=${eddData.daysDiff}`],
                        eddDate: eddData.eddDate,
                        pageName: digitalData.page.attributes.sephoraPageInfo.pageName,
                        pageType: digitalData.page.category.pageType
                    }
                });

                // Set digitalData so eddNotYetCaptured check works correctly
                digitalData.page.attributes.eddDate = eddData.eddDate;
            }
        }
    };

    componentDidMount() {
        // GUAR-8124: Detect SPA navigation by checking if we navigated from another product page
        // On SPA navigation, the component unmounts/remounts but digitalData persists
        const hadPreviousEddDate = !!digitalData.page.attributes.eddDate;
        const cameFromProductPage = digitalData.page.attributes.previousPageData?.pageType === analyticsConsts.PAGE_TYPES.PRODUCT;
        delete digitalData.page.attributes.eddDate;

        // If we came from another product page, this is SPA navigation (not initial page load)
        if (hadPreviousEddDate || cameFromProductPage) {
            this.isInitialPageLoad = false;
        }

        store.setAndWatch('page.product', this, null, true);
        store.setAndWatch('page.product.isUserSpecificReady', this, userSpecificFlag => {
            if (userSpecificFlag?.isUserSpecificReady) {
                this.initializeAnalyticsIfAllDataReady(USER_SPECIFIC_DATA_READY);
            }
        });

        // Wait for the initial personalized product gets into the store
        Events.onLastLoadEvent(window, [Events.ProductInfoReady], () => {
            userUtils.validateUserStatus().then(() => {
                store.setAndWatch('user', this, (newData, oldData) => {
                    const userIsUpdatedForTheFirstTime = newData.user && !oldData;
                    const userProfileHasChanged = newData.user.profileId !== oldData?.user.profileId;

                    if (!userIsUpdatedForTheFirstTime && userProfileHasChanged) {
                        store.dispatch(ProductActions.updateProductWithUserSpecificData());
                    }

                    // Make sure we call `addToBasketFromURLParameter` method just once,
                    // regardless of new user data
                    if (this.state.alreadyTriedToAddProductToBasket === false) {
                        this.setState({
                            alreadyTriedToAddProductToBasket: true
                        });

                        // This is the spot where it's safe to call this method, giving that
                        // for basket inclusion, we need to have user and product data at hand.
                        this.addToBasketFromURLParameter();
                    }
                });
            });
        });

        const showStickyMenu = Sephora.isMobile() && window.matchMedia(breakpoints.xsMax).matches;
        this.setState({ showStickyMenu });

        Sephora.Util.onLastLoadEvent(window, [PostLoad], () => {
            const product = this.state.product;

            if (!product?.currentSku?.isOutOfStock) {
                this.addItemsFromTsl();
            }

            // GUAR-8124: Only fire EDD analytics on initial page load, not on SPA navigation
            // The PostLoad event can fire multiple times, but we only want to track initial load here
            // SPA navigation is handled in componentDidUpdate
            // Guard: skip if eddDate was already captured (e.g., by fulfillmentOptionsJustArrived handler)
            if (this.isInitialPageLoad && !digitalData.page.attributes.eddDate) {
                this.fireEddDateAnalytics(product);
            }
        });
    }

    componentDidUpdate(_prevProps, prevState) {
        const productIdChanged = prevState.product?.productDetails?.productId !== this.state.product?.productDetails?.productId;

        if (productIdChanged) {
            this.shouldSendAnalytics[USER_SPECIFIC_DATA_READY] = false;

            // GUAR-8124: Mark that we're now in SPA navigation mode (not initial page load)
            this.isInitialPageLoad = false;

            // GUAR-8124: Clear old eddDate during SPA navigation
            delete digitalData.page.attributes.eddDate;

            // GUAR-8124: Fire ASYNC_PAGE_LOAD for SPA navigation
            this.fireEddDateAnalytics(this.state.product);
        }

        // Swatch Changes require to update the digitalData.product value. Otherwise it is empty on every change.
        if (prevState.product?.currentSku?.displayName !== this.state.product?.currentSku?.displayName) {
            productPageBindings.initializeAnalyticsObjectWithProductData();
        }

        // GUAR-8124: Handle late-arriving fulfillmentOptions
        // This fires when fulfillmentOptions loads after the product has already been set
        const fulfillmentOptionsJustArrived = !prevState.product?.fulfillmentOptions && !!this.state.product?.fulfillmentOptions;
        const eddNotYetCaptured = !digitalData.page.attributes.eddDate;

        if (fulfillmentOptionsJustArrived && eddNotYetCaptured) {
            this.fireEddDateAnalytics(this.state.product);
        }
    }

    componentWillUnmount() {
        // GUAR-8124: On un-mount remove eddDate from next calls
        digitalData.page.attributes.eddDate && delete digitalData.page.attributes.eddDate;
    }

    shouldComponentUpdate(_nextProps, nextState) {
        const {
            product: { currentSku, errorCode, productDetails }
        } = this.state;
        const currentSkuType = skuUtils.getProductType(currentSku);
        const nextSkuType = skuUtils.getProductType(nextState.product.currentSku);

        return (
            nextState.product.errorCode !== errorCode ||
            nextSkuType !== currentSkuType ||
            nextState.product.productDetails?.productId !== productDetails?.productId
        );
    }

    addItemsFromTsl = () => {
        const currentUrl = window.location.href;
        const { product } = this.state;
        const currentSku = product.currentSku;

        if (currentUrl.includes('c3ch=Linkshare') && currentUrl.includes(`c3nid=${Pages.tslId}`)) {
            try {
                store.dispatch(
                    AddToBasketActions.addToBasket(currentSku, BasketType.Standard, ADD_TO_BASKET_DEFAULT_QTY, () => {
                        if (window.matchMedia(breakpoints.smMin).matches) {
                            store.dispatch(
                                actions.showAddToBasketModal({
                                    product: product,
                                    sku: currentSku,
                                    quantity: ADD_TO_BASKET_DEFAULT_QTY,
                                    isOpen: true
                                })
                            );
                        }
                    })
                );
            } catch {
                store.dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        message: getBasketText('genericErrorTitle'),
                        buttonText: getBasketText('gotIt')
                    })
                );
            }
        }
    };

    // Extracted helper to determine pageType and content for a given SKU
    determinePageTypeAndContent = currentSku => {
        const currentSkuType = skuUtils.getProductType(currentSku);
        let pageType = analyticsConsts.PAGE_TYPES.PRODUCT;
        let content;

        switch (currentSkuType) {
            case ROUGE_REWARD_CARD: {
                const { rewardPoints, rewardValue } = currentSku;
                let subtitle = '';

                if (rewardPoints && rewardValue) {
                    subtitle = getText('subtitle', [rewardPoints, rewardValue]);
                }

                pageType = analyticsConsts.PAGE_TYPES.REWARDS;
                content = (
                    <DigitalProduct
                        subtitle={subtitle}
                        processSkuId={this.processSkuId}
                    />
                );

                break;
            }
            case REWARD: {
                pageType = analyticsConsts.PAGE_TYPES.REWARDS;
                content = <RewardProduct processSkuId={this.processSkuId} />;

                break;
            }
            case SDU: {
                content = (
                    <SDUProductPage
                        skipConfirmationModal={true}
                        processSkuId={this.processSkuId}
                    />
                );

                break;
            }
            case STANDARD:
            default: {
                content = <RegularProduct processSkuId={this.processSkuId} />;

                break;
            }
        }

        return { pageType, content };
    };

    // Extracted helper to detect face out variation and resolve variation id
    getVariationInfo = (product, currentSku) => {
        const hasFaceoutVariation =
            (Array.isArray(product?.skus) && product.skus.length > 1) ||
            !!(currentSku?.variationValue || currentSku?.variation_type || currentSku?.variation_id || currentSku?.color || currentSku?.size);

        const variationId =
            currentSku?.variation_id ||
            currentSku?.data?.variation_id ||
            currentSku?.variationId ||
            (hasFaceoutVariation ? currentSku?.skuId : undefined);

        return { hasFaceoutVariation, variationId };
    };

    render() {
        // This should be broken down and accessed from subcomponents that need specific
        // data rather than being pass through the high level page components
        const {
            product,
            product: { currentSku }
        } = this.state;

        // Determine page type and content via helper to reduce render complexity
        const { pageType, content } = this.determinePageTypeAndContent(currentSku);
        const showStickyMenu = skuUtils.getProductType(currentSku) === STANDARD && this.state.showStickyMenu;

        // Variation info (face-out + variation id) moved to helper
        const { hasFaceoutVariation, variationId } = this.getVariationInfo(product, currentSku);

        // Resolve product display name for Constructor attribute
        const productDisplayName = product?.productDetails?.displayName || currentSku?.productName || product?.productName || undefined;

        // Simple item price: prefer salePrice, fallback to listPrice (omit attribute if neither)
        const productPrice = currentSku?.salePrice || currentSku?.listPrice || undefined;

        return (
            <Container
                is='main'
                data-at={Sephora.debug.dataAt('pdp_page_container')}
                data-cnstrc-product-detail
                data-cnstrc-item-id={product?.productDetails?.productId || undefined}
                data-cnstrc-item-name={productDisplayName}
                data-cnstrc-item-price={productPrice}
                {...(hasFaceoutVariation && variationId ? { 'data-cnstrc-item-variation-id': variationId } : {})}
            >
                {showStickyMenu && <MobileStickyMenu />}
                <SetPageAnalyticsProps
                    pageType={pageType}
                    pageName={product?.productDetails?.productId}
                />
                {content}
                {!showStickyMenu && <BackToTopButton analyticsLinkName={analyticsConsts.LinkData.BACK_TO_TOP} />}
                <ProductPageSEO product={product} />
            </Container>
        );
    }

    initializeAnalyticsIfAllDataReady = keyThatsReady => {
        if (Object.values(this.shouldSendAnalytics).every(x => x)) {
            return; // already sent
        }

        this.shouldSendAnalytics[keyThatsReady] = true;

        if (Object.values(this.shouldSendAnalytics).every(x => x)) {
            productPageBindings.initializeAnalyticsObjectWithProductData();
        }
    };

    processSkuId = component => {
        const {
            page: {
                product,
                product: { productDetails }
            }
        } = store.getState();
        let skuId = urlUtils.getParamsByName(SKU_ID_PARAM);
        component.isSkuReady = true;
        const dispatchParams = { disableDispatchWarning: true };

        if (Array.isArray(skuId) && skuId.length > 0) {
            skuId = skuId[0];

            // TODO: Remove sample specific code if we kill sample ppages.  Otherwise, find more elegant
            // solution (separate component for samples?
            // If we have a sample, we have to get the full product data
            // Note: we check by sample product id here because we don't have the full sku data to
            // check skuType
            if ((productDetails || product).productId === SAMPLE) {
                store.dispatch(ProductActions.fetchCurrentProduct(SAMPLE, skuId), dispatchParams);
            } else {
                const skuFromProduct = skuHelpers.getSkuFromProduct(product, skuId);

                // Confirm that this sku is present in the product
                if (skuFromProduct) {
                    store.dispatch(ProductActions.updateSkuInCurrentProduct(skuFromProduct, QUERY_STRING), dispatchParams);
                }
            }
        }

        // this call should be made at the end of this method, so at this point all SKU data will be updated and ready to use
        this.initializeAnalyticsIfAllDataReady(SKU_DATA_READY);
    };

    addToBasketSuccess = () => {
        Location.navigateTo(null, '/basket');
    };

    addToBasketFailure = error => {
        store.dispatch(Actions.showInterstice(false));

        if (error.errorMessages) {
            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    message: error.errorMessages[0],
                    buttonText: getBasketText('gotIt')
                })
            );
        }
    };

    addToBasketFromURLParameter = async () => {
        const addToBasketURLQuery = urlUtils.getParamsByName(ADD_TO_BASKET_PARAM);
        const addToBasketURLParam = addToBasketURLQuery ? addToBasketURLQuery[0] : null;

        // If no valid URL param, skip functionality
        if (!addToBasketURLParam) {
            return;
        }

        const { product } = this.state;
        const { basket } = store.getState();
        const currentSku = product.currentSku;

        // Before proceeding, we should see if URL param is appropriate
        if (
            ADD_TO_BASKET_MAPPING[ADD_TO_BASKET_PARAM] &&
            ADD_TO_BASKET_MAPPING[ADD_TO_BASKET_PARAM].find(mappedParam => mappedParam === addToBasketURLParam)
        ) {
            // Make sure product is not already in basket
            if (Array.isArray(basket.items) && basket.items.find(basketItem => basketItem.sku.skuId === currentSku.skuId)) {
                store.dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        message: getBasketText('alreadyInCart'),
                        buttonText: getBasketText('gotIt')
                    })
                );

                return;
            }

            try {
                store.dispatch(Actions.showInterstice(true));

                await store.dispatch(AddToBasketActions.addToBasket(currentSku, false, ADD_TO_BASKET_DEFAULT_QTY, this.addToBasketSuccess));
            } catch (e) {
                this.addToBasketFailure(e);
            }
        }
    };
}

export default wrapComponent(ProductPage, 'ProductPage', true);
